import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type DocumentCategory = 'hr' | 'training' | 'procedures' | 'legal' | 'general';

export interface CorporateDocument {
  id: string;
  title: string;
  description: string;
  type: 'guide' | 'policy' | 'procedure' | 'training';
  category: DocumentCategory;
  url: string;
  file_path?: string; // Firebase Storage path for uploaded files
  created_at: string;
  created_by: string;
  created_by_id: string;
}

interface NewDocument {
  title: string;
  description: string;
  url: string;
  type: CorporateDocument['type'];
  category: DocumentCategory;
  file?: File;
}

const COLLECTION_NAME = 'corporateDocuments';
const STORAGE_PATH = 'corporate-documents';

export function useCorporateDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<CorporateDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Subscribe to Firestore documents
  useEffect(() => {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs: CorporateDocument[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        docs.push({
          id: docSnap.id,
          title: data.title,
          description: data.description,
          type: data.type,
          category: data.category || 'general',
          url: data.url,
          file_path: data.file_path,
          created_at: data.created_at instanceof Timestamp 
            ? data.created_at.toDate().toISOString().split('T')[0]
            : data.created_at,
          created_by: data.created_by,
          created_by_id: data.created_by_id,
        });
      });
      setDocuments(docs);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching corporate documents:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Upload file to Firebase Storage
  const uploadFile = useCallback(async (file: File): Promise<{ url: string; path: string }> => {
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `${STORAGE_PATH}/${timestamp}_${safeName}`;
    const storageRef = ref(storage, filePath);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    return { url, path: filePath };
  }, []);

  // Add a new document
  const addDocument = useCallback(async (newDoc: NewDocument): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to add documents');
      return false;
    }

    if (!newDoc.title) {
      toast.error('Title is required');
      return false;
    }

    setUploading(true);

    try {
      let fileUrl = newDoc.url;
      let filePath: string | undefined;

      // Upload file if provided
      if (newDoc.file) {
        const uploadResult = await uploadFile(newDoc.file);
        fileUrl = uploadResult.url;
        filePath = uploadResult.path;
      }

      if (!fileUrl) {
        toast.error('URL or file is required');
        setUploading(false);
        return false;
      }

      await addDoc(collection(db, COLLECTION_NAME), {
        title: newDoc.title,
        description: newDoc.description,
        type: newDoc.type,
        category: newDoc.category,
        url: fileUrl,
        file_path: filePath || null,
        created_at: Timestamp.now(),
        created_by: user.displayName || user.email || 'Admin',
        created_by_id: user.id,
      });

      toast.success('Document added successfully');
      setUploading(false);
      return true;
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error('Failed to add document');
      setUploading(false);
      return false;
    }
  }, [user, uploadFile]);

  // Delete a document
  const deleteDocument = useCallback(async (document: CorporateDocument): Promise<boolean> => {
    try {
      // Delete file from Storage if it exists
      if (document.file_path) {
        try {
          const fileRef = ref(storage, document.file_path);
          await deleteObject(fileRef);
        } catch (storageError) {
          console.warn('Could not delete file from storage:', storageError);
        }
      }

      // Delete document from Firestore
      await deleteDoc(doc(db, COLLECTION_NAME, document.id));
      
      toast.success('Document deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
      return false;
    }
  }, []);

  return {
    documents,
    loading,
    uploading,
    addDocument,
    deleteDocument,
  };
}
